/**
 * Колесо мыши там, где оно означает не «прокрутить страницу».
 *
 * Два случая, и оба про одно: у колеса под курсором может быть смысл ближе,
 * чем прокрутка всей страницы. Над календарём естественно менять масштаб, над
 * доской — двигать колонки вбок, потому что вертикально двигать нечего.
 *
 * Общее правило у обоих одно и оно важнее самих жестов: **элемент забирает
 * колесо только пока ему есть что с ним делать**. Дошли до предела — событие
 * не перехватывается, и страница прокручивается как обычно. Иначе календарь
 * посреди длинной страницы превращался бы в ловушку: курсор попал на него, и
 * страница встала.
 *
 * Обработчик вешается вручную, а не через `onWheel` в разметке: React
 * подписывается на колесо пассивно, и `preventDefault` из такого обработчика
 * не работает — браузер прокрутит страницу всё равно.
 *
 * Ctrl и Cmd не трогаются никогда: это масштаб браузера и системный жест,
 * и перехватывать их — значит ломать то, чем человек пользуется везде.
 */

import { useEffect, useRef, type RefObject } from 'react';

/**
 * Накопленный поворот, после которого масштаб меняется на шаг.
 *
 * Сто — это примерно один щелчок колеса мыши: щёлкнул раз — сдвинул масштаб
 * на единицу, и никакой задержки между щелчками. Трекпад шлёт события мелкими
 * порциями, и один размашистый жест сдвинет масштаб на несколько ступеней —
 * но их всего пять, дальше жест возвращается странице, так что уехать
 * непоправимо далеко нельзя.
 *
 * Пауза после шага была бы честнее для трекпада и хуже для мыши: два быстрых
 * щелчка подряд — обычное дело, и терять второй нельзя.
 */
const STEP_THRESHOLD = 100;

/** Через сколько тишины накопленное забывается. */
const ACCUMULATOR_TTL_MS = 400;

/**
 * Колесо меняет что-то на шаг: масштаб, уровень, номер.
 *
 * `direction` равен −1 при повороте вверх и +1 при повороте вниз — как в
 * любом просмотрщике: от себя мельче, к себе крупнее. Что это значит для
 * конкретного экрана, решает вызывающий.
 *
 * `canStep` спрашивается до перехвата: он же отвечает на вопрос «забирать ли
 * событие». Без него календарь на пределе масштаба съедал бы прокрутку.
 */
export function useWheelStep(
  ref: RefObject<HTMLElement | null>,
  onStep: (direction: 1 | -1) => void,
  canStep: (direction: 1 | -1) => boolean,
): void {
  // Через ref, а не через зависимости эффекта: обработчик переподписывался бы
  // на каждый рендер, и накопленное сбрасывалось бы посреди жеста.
  const handlers = useRef({ onStep, canStep });
  handlers.current = { onStep, canStep };

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    let accumulated = 0;
    let lastEventAt = 0;

    const onWheel = (event: WheelEvent) => {
      if (event.ctrlKey || event.metaKey) return;
      // Горизонтальный жест трекпада к масштабу отношения не имеет.
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
      if (event.deltaY === 0) return;

      const now = event.timeStamp;
      const direction: 1 | -1 = event.deltaY > 0 ? 1 : -1;

      if (now - lastEventAt > ACCUMULATOR_TTL_MS) accumulated = 0;
      // Смена направления посреди жеста — это новый жест.
      if (accumulated !== 0 && Math.sign(accumulated) !== direction) accumulated = 0;
      lastEventAt = now;

      if (!handlers.current.canStep(direction)) return;

      // Событие забрано: дальше страница не двигается, пока есть куда
      // масштабировать.
      event.preventDefault();

      accumulated += event.deltaY;
      if (Math.abs(accumulated) < STEP_THRESHOLD) return;

      accumulated = 0;
      handlers.current.onStep(direction);
    };

    node.addEventListener('wheel', onWheel, { passive: false });
    return () => node.removeEventListener('wheel', onWheel);
  }, [ref]);
}

/**
 * Вертикальное колесо двигает содержимое вбок.
 *
 * Нужно доске: колонки уходят за правый край, а вертикально там двигать
 * нечего. Мышь без горизонтального колеса — самый обычный случай, и без
 * этого до правых колонок добираются полосой прокрутки внизу экрана.
 *
 * Порядок уступок здесь важнее самого жеста:
 *
 *   1. Если под курсором колонка, которой ещё есть куда прокрутиться в ту же
 *      сторону, — колесо принадлежит ей. Иначе, читая длинную колонку, человек
 *      вместо прокрутки уезжал бы вбок.
 *   2. Если колонка кончилась, вертикальный поворот двигает доску вбок.
 *   3. Если и доска на пределе, событие не перехватывается: страница
 *      прокручивается как обычно.
 */
export function useWheelPan(ref: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const onWheel = (event: WheelEvent) => {
      if (event.ctrlKey || event.metaKey) return;
      // Горизонтальный жест трекпада браузер обработает сам и лучше.
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
      if (event.deltaY === 0) return;
      if (node.scrollWidth <= node.clientWidth) return;

      if (scrollableAncestor(event.target, node, event.deltaY)) return;

      const before = node.scrollLeft;
      node.scrollLeft += event.deltaY;
      // Двинулись — значит событие наше. Не двинулись (край) — отдаём странице.
      if (node.scrollLeft !== before) event.preventDefault();
    };

    node.addEventListener('wheel', onWheel, { passive: false });
    return () => node.removeEventListener('wheel', onWheel);
  }, [ref]);
}

/**
 * Есть ли между курсором и границей то, что само прокрутится в эту сторону.
 *
 * Проверяется не «можно ли прокручивать вообще», а «есть ли запас именно в
 * эту сторону»: колонка, докрученная до низа, уступает жест доске, а не
 * держит его до последнего.
 */
function scrollableAncestor(
  target: EventTarget | null,
  boundary: HTMLElement,
  delta: number,
): boolean {
  let node = target instanceof Element ? target : null;

  while (node && node !== boundary) {
    if (node instanceof HTMLElement) {
      const overflow = getComputedStyle(node).overflowY;
      const scrolls = overflow === 'auto' || overflow === 'scroll';
      if (scrolls && node.scrollHeight > node.clientHeight) {
        const room =
          delta > 0
            ? node.scrollHeight - node.clientHeight - node.scrollTop > 1
            : node.scrollTop > 1;
        if (room) return true;
      }
    }
    node = node.parentElement;
  }

  return false;
}
